import { Loader } from "@mantine/core";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { Archive, Images, TextSelect, Video } from "lucide-react";
import { useState } from "react";

import {
  getListJobsQueryKey,
  useGetCatalog,
  useGetJob,
} from "../../api/generated/endpoints/generation/generation";
import { getGetAssetQueryOptions, useGetAsset } from "../../api/generated/endpoints/assets/assets";
import type { Job, SourceRef } from "../../api/generated/model";
import { isApiError } from "../../api/errors";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { pictureOf, type PickedImage } from "../../platform/media/ImagePicker";
import { PageColumns } from "../../ui/PageColumns";
import { TypeTabs } from "../../ui/TypeTabs";
import { AdCopyTab } from "../../platform/studio/ad-copies/AdCopyTab";
import { Composer } from "../../platform/studio/composer/Composer";
import { cloneAssetIds, cloneSource, typesFor, type Medium } from "../../platform/studio/draft";
import type { DraftShape } from "../../platform/studio/draft-state";
import { MyGenerations } from "./MyGenerations";
import { jobMedium } from "../../platform/jobs/detail/outputs";
import { StudioLibrary } from "./StudioLibrary";

const ROUTE = "/signed-in/teams/$teamId/brands/$brandId/generate";
// The Image page's third tab: the brand's ad copies (#234, live's `text-copy-tab`).
const AD_COPY = { value: "copy", label: "Ad copy", icon: <TextSelect size={16} /> };

/**
 * The studio of a brand (design-system.md 3.3): the Image or the Video page, with the brand's
 * library and My Generations as tabs and the floating composer at the bottom; the Image page has
 * a third tab, live's Ad copy, with no composer (#234). With
 * `?job=&output=` in the URL, a job starts from that delivered image (a tweak or a video); with
 * `?clone=`, the composer starts from that earlier job's draft; with `?advanced=`, in live's
 * Advanced mode with that library image as its reference; with `?layerize=`, in live's Layerize
 * mode on it. A started job shows at the top of My Generations; the page does not navigate away.
 */
export function StudioScreen() {
  const { teamId, brandId } = useParams({ from: ROUTE });
  const search = useSearch({ from: ROUTE });
  const navigate = useNavigate({ from: "/teams/$teamId/brands/$brandId/generate" });
  const queryClient = useQueryClient();
  // The composer's count and ratio, which the empty My Generations shows its cards at.
  const [shape, setShape] = useState<DraftShape>({ outputs: 1, aspectRatio: null });
  // The composer waits for the job to clone; a failed read leaves it the model's defaults.
  const cloned = useGetJob(teamId, search.clone ?? "", {
    query: { enabled: search.clone !== undefined },
  });
  const clone = search.clone === undefined ? undefined : cloned.data;
  // And for the library files it took: read for their pictures, and those gone since (in the
  // trash, deleted) left out of the draft, which the server would refuse (issue 828, item 23).
  const files = useCloneFiles(teamId, brandId, clone);
  const waiting = search.clone !== undefined && (cloned.isPending || files.pending);
  // The library's Clone and Layerize name an image for live's Advanced or Layerize mode (issue
  // 828, item 23): the composer waits for its picture.
  const linkedImage = useLibraryImage(teamId, brandId, search.advanced ?? search.layerize);
  // "Change this image" and "Make a video of it" name a delivered output (G1 decision 21), or a
  // library file (its menu's Edit and Animate, S1 decision 22).
  // A video's Edit (S1c) names its video for the tool, not a frame.
  const editsVideo = search.type === "video.edit";
  const linked: SourceRef | null =
    search.asset !== undefined && !editsVideo
      ? { kind: "asset", asset_id: search.asset }
      : search.job === undefined || search.output === undefined
        ? null
        : { kind: "job_output", job_id: search.job, output_index: search.output };
  const source: SourceRef | null = clone === undefined ? linked : cloneSource(clone, files.kept);
  // A video from an image is the Video page's, even when the link names only the type; a clone
  // is on its job's page unless the link names one.
  const linkedMedium: Medium =
    search.type === "video.image" || editsVideo ? "video" : "image";
  const medium: Medium =
    search.medium ?? (clone === undefined ? linkedMedium : (jobMedium(clone) ?? linkedMedium));
  // The Video page has no Ad copy: a link that names it opens the library.
  const view = search.view === "copy" && medium === "video" ? "library" : (search.view ?? "library");
  const catalog = useGetCatalog();
  const data = catalog.data;
  const usable = data !== undefined && typesFor(data, source !== null, medium).length > 0;
  const show = (next: string) =>
    void navigate({
      search: (current) => ({
        ...current,
        view: next === "generations" || next === "copy" ? next : undefined,
      }),
      replace: true,
    });
  const started = async () => {
    // The team's job lists hold the new job once read again; My Generations is one of them.
    await queryClient.invalidateQueries({ queryKey: [getListJobsQueryKey(teamId)[0]] });
    show("generations");
  };
  // Until the job to clone is read, its page is unknown: the page waits rather than show the
  // Image page (and read its files) and then switch to the Video page.
  if (waiting && search.medium === undefined) {
    return (
      <PageColumns>
        <Loader size="sm" aria-label="Loading the job to clone" />
      </PageColumns>
    );
  }
  return (
    <PageColumns
      top={
        <TypeTabs
          label={medium === "video" ? "Video" : "Image"}
          value={view}
          onChange={show}
          tabs={[
            { value: "library", label: "Library", icon: <Archive size={16} /> },
            {
              value: "generations",
              label: "My Generations",
              icon: medium === "video" ? <Video size={16} /> : <Images size={16} />,
            },
            ...(medium === "image" ? [AD_COPY] : []),
          ]}
        />
      }
      bottom={
        // Live's Ad copy tab has no composer.
        data !== undefined && usable && !waiting && !linkedImage.pending && view !== "copy" ? (
          <Composer
            // A new source from a link opens a new composer; one chosen inside keeps the prompt.
            // Another brand opens a new one too: the old brand's product, references and applied
            // prompt template mean nothing under it (BrandSelect, G12).
            key={`${brandId} ${medium} ${search.clone ?? ""} ${search.job ?? ""}:${search.output ?? ""} ${search.asset ?? ""} ${search.advanced ?? ""} ${search.layerize ?? ""}`}
            catalog={data}
            medium={medium}
            initialSource={source}
            initialType={clone?.type ?? search.type}
            initialVideo={editsVideo ? search.asset : undefined}
            clone={clone}
            cloneKept={files.kept}
            cloneLeftOut={files.gone}
            initialAdvanced={search.advanced === undefined ? undefined : linkedImage.picked}
            initialLayerize={search.advanced !== undefined ? undefined : linkedImage.picked}
            onStarted={() => void started()}
            onShapeChange={setShape}
          />
        ) : null
      }
    >
      {catalog.isPending ? <Loader size="sm" aria-label="Loading the catalog" /> : null}
      {catalog.isError ? <ErrorAlert error={catalog.error} /> : null}
      {cloned.isError ? <ErrorAlert error={cloned.error} /> : null}
      {linkedImage.error === null ? null : <ErrorAlert error={linkedImage.error} />}
      {view === "library" ? (
        <StudioLibrary teamId={teamId} brandId={brandId} medium={medium} />
      ) : null}
      {view === "generations" ? (
        <MyGenerations teamId={teamId} brandId={brandId} medium={medium} shape={shape} />
      ) : null}
      {view === "copy" ? <AdCopyTab teamId={teamId} brandId={brandId} /> : null}
    </PageColumns>
  );
}

/**
 * A library image a link names for a mode of the composer, read for its picture: `picked` once
 * read, or undefined while it is read, when the read fails, or for one in the trash or not ready,
 * which opens the composer as it is.
 */
function useLibraryImage(teamId: string, brandId: string, assetId: string | undefined) {
  const read = useGetAsset(teamId, brandId, assetId ?? "", { query: { enabled: assetId !== undefined } });
  const file = assetId === undefined ? undefined : read.data;
  const picked: PickedImage | undefined =
    file === undefined || file.kind !== "image" || file.deleted_at !== null || file.status !== "ready"
      ? undefined
      : {
          assetId: file.id,
          url: pictureOf(file),
          original: file.urls.original,
          name: file.name,
          width: file.width,
          height: file.height,
        };
  return {
    picked,
    pending: assetId !== undefined && read.isPending,
    error: assetId === undefined ? null : read.error,
  };
}

/**
 * A clone's library files, read before its composer opens: `kept` says which it takes back, and
 * `gone` counts those in the trash or deleted since. A read that fails otherwise keeps the file.
 */
function useCloneFiles(teamId: string, brandId: string, clone: Job | undefined) {
  const ids = clone === undefined ? [] : cloneAssetIds(clone);
  const reads = useQueries({ queries: ids.map((id) => ({ ...getGetAssetQueryOptions(teamId, brandId, id), retry: false })) });
  const goneIds = new Set(
    ids.filter((_, at) => {
      const read = reads[at]!;
      if (read.data !== undefined) return read.data.deleted_at !== null || read.data.status !== "ready";
      return isApiError(read.error, "NOT_FOUND");
    }),
  );
  return {
    pending: reads.some((read) => read.isPending),
    kept: (assetId: string) => !goneIds.has(assetId),
    gone: goneIds.size,
  };
}
